import React, { useEffect, useState, useCallback } from "react";
import { api, API, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, Upload, ExternalLink, RotateCcw, UploadCloud, CheckCircle, XCircle, Plus, Pencil, Trash2, Save, X as XIcon, FolderPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const STATUS_ICONS = {
  pending: <Clock size={14} className="text-[#050303]/40" />,
  submitted: <Upload size={14} className="text-[#bf858e]" />,
  approved: <CheckCircle2 size={14} className="text-green-600" />,
};

export default function Compliance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState({ deals: [], pending_approvals: 0 });
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [items, setItems] = useState([]);
  const [dealInfo, setDealInfo] = useState(null);
  const [pending, setPending] = useState([]);
  const [newTxnOpen, setNewTxnOpen] = useState(false);

  const loadSummary = () => api.get("/compliance/summary").then((r) => setSummary(r.data));
  const loadPending = () => api.get("/compliance/pending").then((r) => setPending(r.data));
  const loadDeal = (id) => api.get(`/compliance/deals/${id}/items`).then((r) => {
    setItems(r.data.items); setDealInfo(r.data.deal);
  });

  useEffect(() => { loadSummary(); if (isAdmin) loadPending(); }, [isAdmin]);
  useEffect(() => { if (selectedDealId) loadDeal(selectedDealId); }, [selectedDealId]);

  const uploadForItem = async (itemId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/compliance/items/${itemId}/upload`, {
        storage_path: data.path, url: data.url, original_filename: file.name,
      });
      toast.success("Document submitted");
      if (selectedDealId) loadDeal(selectedDealId);
      loadSummary();
    } catch { toast.error("Upload failed"); }
  };

  const approve = async (itemId) => {
    await api.post(`/compliance/items/${itemId}/approve`);
    toast.success("Approved");
    loadPending(); loadSummary();
    if (selectedDealId) loadDeal(selectedDealId);
  };
  const reset = async (itemId) => {
    if (!window.confirm("Reset this item so the agent re-uploads?")) return;
    await api.post(`/compliance/items/${itemId}/reset`);
    toast.success("Reset — agent will re-upload");
    loadPending(); loadSummary();
    if (selectedDealId) loadDeal(selectedDealId);
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.stage] = acc[it.stage] || []).push(it); return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="compliance-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Brokerage · Residential Transactions</div>
          <h1 className="font-heading text-5xl mt-2">Compliance Checklist</h1>
          <p className="text-sm text-[#050303]/60 mt-2">
            Master checklist for every residential deal — 3 sections, 17 documents.
          </p>
        </div>
        {(
          <button
            onClick={() => setNewTxnOpen(true)}
            className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2"
            data-testid="new-txn-btn"
          >
            <FolderPlus size={16} /> New Compliance Transaction
          </button>
        )}
      </header>

      <NewTransactionDialog
        open={newTxnOpen}
        setOpen={setNewTxnOpen}
        onSelect={(dealId) => { setSelectedDealId(dealId); loadSummary(); }}
      />

      {isAdmin && (
        <section className="core-card p-6" data-testid="admin-pending-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Pending Approvals</div>
              <div className="font-heading text-3xl mt-1">{pending.length}</div>
            </div>
            <button onClick={loadPending} className="text-xs text-[#bf858e] hover:text-[#050303]">Refresh</button>
          </div>
          {pending.length === 0 && <div className="text-sm text-[#050303]/50 py-6 text-center">Nothing to review right now.</div>}
          <div className="divide-y divide-[#d1cece]">
            {pending.map((p, idx) => (
              <div key={`${p.id}-${p.template_id}-${idx}`} className="py-3 flex items-center gap-4 flex-wrap" data-testid={`pending-${p.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="text-sm font-semibold">{p.title}</div>
                  <div className="text-xs text-[#050303]/60">
                    {p.stage} · {p.deal_title} · {p.agent_name}
                  </div>
                </div>
                {p.document?.url && (
                  <a href={p.document.url} target="_blank" rel="noreferrer"
                    className="text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`pending-view-${p.id}`}>
                    <ExternalLink size={12} /> View
                  </a>
                )}
                <button onClick={() => approve(p.id)}
                  className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e]" data-testid={`approve-${p.id}`}>
                  <CheckCircle2 size={13} className="inline mr-1" /> Approve
                </button>
                <button onClick={() => reset(p.id)}
                  className="text-xs text-[#050303]/50 hover:text-[#bf858e]" data-testid={`reset-${p.id}`}>
                  <RotateCcw size={12} className="inline" /> Ask re-upload
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {isAdmin && <ChecklistManager />}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {summary.deals?.map((d) => (
          <button key={d.deal_id}
            onClick={() => setSelectedDealId(d.deal_id)}
            className={`core-card p-5 text-left hover:shadow-md transition-all ${selectedDealId === d.deal_id ? "ring-2 ring-[#050303]" : ""}`}
            data-testid={`deal-card-${d.deal_id}`}>
            <div className="overline">{d.stage}</div>
            <div className="font-heading text-lg mt-1 truncate">{d.title}</div>
            {isAdmin && d.agent_name && (
              <div className="text-[10px] uppercase tracking-wider text-[#bf858e] mt-0.5">{d.agent_name}</div>
            )}
            <div className="mt-3 h-2 bg-[#e1b7c1] rounded-full overflow-hidden">
              <div className="h-full bg-[#bf858e]" style={{ width: `${d.pct}%` }} />
            </div>
            <div className="text-xs text-[#050303]/60 mt-1">
              {d.approved}/{d.total} approved · {d.submitted} pending review
            </div>
          </button>
        ))}
      </section>

      {items.length > 0 && (
        <section className="core-card p-6 space-y-5" data-testid="deal-items-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline">{dealInfo?.stage}</div>
              <h2 className="font-heading text-2xl mt-1">{dealInfo?.title}</h2>
              <p className="text-xs text-[#050303]/60">{dealInfo?.address} · {fmtMoney(dealInfo?.price)}</p>
            </div>
            {isAdmin && items.some((it) => it.status === "submitted") && (
              <button
                onClick={async () => {
                  const n = items.filter((it) => it.status === "submitted").length;
                  if (!window.confirm(`Approve all ${n} submitted row${n === 1 ? "" : "s"} on this deal? This can't be undone individually — use the reset button per row if you need to reverse a single approval.`)) return;
                  try {
                    const r = await api.post(`/compliance/deals/${selectedDealId}/approve_all`);
                    toast.success(`${r.data.approved} row${r.data.approved === 1 ? "" : "s"} approved`);
                    loadDeal(selectedDealId);
                    loadSummary();
                  } catch {
                    toast.error("Approve-all failed");
                  }
                }}
                className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e] flex items-center gap-1"
                data-testid="approve-all-btn"
              >
                <CheckCircle2 size={13} /> Approve all {items.filter((it) => it.status === "submitted").length} submitted
              </button>
            )}
          </div>

          <BulkDropZone dealId={selectedDealId} items={items} onDone={() => { loadDeal(selectedDealId); loadSummary(); }} />

          {Object.entries(grouped).map(([stage, list]) => (
            <div key={stage}>
              <div className="text-xs uppercase tracking-wider text-[#050303]/70 mb-2">{stage}</div>
              <div className="divide-y divide-[#d1cece]">
                {list.map((it, idx) => (
                  <div key={`${it.id}-${it.template_id}-${idx}`} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`item-${it.template_id}`}>
                    <div className="w-5">{STATUS_ICONS[it.status]}</div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-sm font-semibold">
                        {it.title} {it.required && <span className="text-[10px] text-red-600 ml-1">REQUIRED</span>}
                      </div>
                      {it.document?.original_filename && (
                        <div className="text-xs text-[#050303]/50 truncate">{it.document.original_filename}</div>
                      )}
                    </div>
                    {it.document?.url && (
                      <a href={it.document.url} target="_blank" rel="noreferrer"
                        className="text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`view-${it.template_id}`}>
                        <ExternalLink size={11} /> View
                      </a>
                    )}
                    {it.status !== "approved" && !isAdmin && (
                      <label className="pill-btn text-xs bg-[#e1b7c1]/40 hover:bg-[#e1b7c1]/70 cursor-pointer" data-testid={`upload-label-${it.template_id}`}>
                        <Upload size={11} className="inline mr-1" />
                        {it.status === "submitted" ? "Replace" : "Upload"}
                        <input type="file" hidden onChange={(e) => e.target.files?.[0] && uploadForItem(it.id, e.target.files[0])}
                          data-testid={`upload-input-${it.template_id}`} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}


function BulkDropZone({ dealId, items, onDone }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setReport(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const token = localStorage.getItem("core_token");
      const resp = await fetch(`${API}/compliance/deals/${dealId}/bulk-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setReport(data);
      const m = data.matched?.length || 0;
      const u = data.unmatched?.length || 0;
      if (m && !u) toast.success(`${m} document${m > 1 ? "s" : ""} routed`);
      else if (m && u) toast.success(`${m} routed · ${u} needs manual upload`);
      else toast.error("No files could be auto-routed");
      onDone?.();
    } catch {
      toast.error("Bulk upload failed");
    } finally {
      setBusy(false);
    }
  }, [dealId, onDone]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="rounded-lg border border-dashed border-[#bf858e]/60 bg-[#e1b7c1]/30" data-testid="bulk-upload-zone">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center px-6 py-8 text-center cursor-pointer transition-colors ${dragging ? "bg-[#e1b7c1]/40" : "hover:bg-[#e1b7c1]/50"} ${busy ? "opacity-60 pointer-events-none" : ""}`}
      >
        <UploadCloud size={26} strokeWidth={1.4} className="text-[#bf858e]" />
        <div className="font-heading text-lg mt-2">Drop all closing docs here</div>
        <div className="text-xs text-[#050303]/60 mt-1 max-w-md">
          {busy ? "Routing files…" : "PDFs, images, or Word docs. Each file is auto-routed to its checklist row by filename (e.g. \"Purchase Sale F20.pdf\" → Under Contract)."}
        </div>
        <input
          type="file"
          multiple
          hidden
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="bulk-upload-input"
        />
      </label>

      {report && (
        <div className="border-t border-[#d1cece] px-5 py-4 space-y-2" data-testid="bulk-upload-report">
          {report.matched?.map((m) => (
            <div key={`m-${m.item_id}`} className="flex items-start gap-2 text-sm" data-testid={`matched-${m.template_id}`}>
              <CheckCircle size={14} className="text-green-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate"><span className="text-[#050303]/60">{m.filename}</span> → <span className="font-semibold">{m.title}</span></div>
                <div className="text-[10px] uppercase tracking-wider text-[#050303]/50">{m.stage}</div>
              </div>
            </div>
          ))}
          {report.unmatched?.map((u, i) => (
            <UnmatchedRow key={`u-${i}`} u={u} items={items} onRouted={(itemId) => {
              // Remove this row + trigger deal refresh in parent
              setReport((r) => r ? {
                ...r,
                unmatched: r.unmatched.filter((x, ix) => ix !== i),
                matched: [...(r.matched || []), {
                  filename: u.filename,
                  item_id: itemId,
                  title: items.find((it) => it.id === itemId)?.title || "",
                  stage: items.find((it) => it.id === itemId)?.stage || "",
                  template_id: items.find((it) => it.id === itemId)?.template_id || "",
                  score: 0,
                }],
              } : r);
              onDone?.();
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

function UnmatchedRow({ u, items, onRouted }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const doRoute = async () => {
    if (!pick) return;
    if (!u.storage_path || !u.url) {
      toast.error("File wasn't stored — please re-upload it");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/compliance/items/${pick}/upload`, {
        storage_path: u.storage_path,
        url: u.url,
        original_filename: u.filename,
      });
      toast.success("Routed");
      onRouted(pick);
    } catch {
      toast.error("Could not route file");
    } finally {
      setBusy(false);
    }
  };

  const canRoute = Boolean(u.storage_path && u.url);

  return (
    <div className="flex items-start gap-2 text-sm" data-testid="unmatched-row">
      <XCircle size={14} className="text-[#bf858e] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate"><span className="font-semibold">{u.filename}</span></div>
        <div className="text-[10px] uppercase tracking-wider text-[#050303]/50">{u.reason}</div>
        {canRoute && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="text-xs border border-[#d1cece] rounded-md px-2 py-1 bg-white max-w-xs"
              data-testid="reroute-select"
            >
              <option value="">Pick a checklist row…</option>
              {["Listing Documents", "Under Contract", "Closed"].map((stage) => {
                const rows = items.filter((it) => it.stage === stage);
                if (!rows.length) return null;
                return (
                  <optgroup key={stage} label={stage}>
                    {rows.map((it) => (
                      <option key={it.id} value={it.id}>{
                        `${it.title}${it.status === "approved" ? " (approved)" : it.status === "submitted" ? " (has file — will replace)" : ""}`
                      }</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <button
              type="button"
              onClick={doRoute}
              disabled={!pick || busy}
              className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reroute-btn"
            >
              {busy ? "Routing…" : "Route to row"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


const STAGE_ORDER = ["Listing Documents", "Under Contract", "Closed"];

function ChecklistManager() {
  const [template, setTemplate] = useState({ stages: STAGE_ORDER, items: [] });
  const [adding, setAdding] = useState(null); // stage name or null
  const [newTitle, setNewTitle] = useState("");
  const [newRequired, setNewRequired] = useState(false);
  const [editing, setEditing] = useState(null); // template id
  const [editTitle, setEditTitle] = useState("");
  const [editRequired, setEditRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = () => api.get("/compliance/template").then((r) => setTemplate(r.data));
  useEffect(() => { load(); }, []);

  const byStage = (s) => template.items.filter((i) => i.stage === s);

  const addRow = async (stage) => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await api.post("/compliance/template", { stage, title: newTitle.trim(), required: newRequired });
      toast.success("Row added");
      setNewTitle(""); setNewRequired(false); setAdding(null);
      await load();
    } catch { toast.error("Could not add row"); }
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    if (!editTitle.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/compliance/template/${id}`, { title: editTitle.trim(), required: editRequired });
      toast.success("Row updated");
      setEditing(null); setEditTitle("");
      await load();
    } catch { toast.error("Could not update row"); }
    finally { setSaving(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Remove "${title}" from the checklist?\n\nExisting documents already uploaded to this row on active deals will be preserved.`)) return;
    setSaving(true);
    try {
      await api.delete(`/compliance/template/${id}`);
      toast.success("Row removed");
      await load();
    } catch { toast.error("Could not remove row"); }
    finally { setSaving(false); }
  };

  return (
    <section className="core-card p-6" data-testid="checklist-manager">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="checklist-manager-toggle"
      >
        <div>
          <div className="overline">Admin</div>
          <div className="font-heading text-2xl mt-1">Manage Checklist Rows</div>
          <div className="text-xs text-[#050303]/60 mt-1">
            Add or rename documents in any section. Rows apply to new deals; existing uploads are preserved.
          </div>
        </div>
        <span className="text-xs text-[#bf858e] uppercase tracking-wider">{expanded ? "Hide" : "Manage"}</span>
      </button>

      {expanded && (
        <div className="mt-6 space-y-6">
          {STAGE_ORDER.map((stage) => (
            <div key={stage} data-testid={`stage-section-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-[#050303]/70">{stage}</div>
                {adding !== stage && (
                  <button
                    onClick={() => { setAdding(stage); setNewTitle(""); setNewRequired(false); }}
                    className="text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                    data-testid={`add-row-btn-${stage.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <Plus size={12} /> Add row
                  </button>
                )}
              </div>
              <div className="divide-y divide-[#d1cece]">
                {byStage(stage).map((it) => {
                  const isEditing = editing === it.id;
                  return (
                    <div key={it.id} className="py-2 flex items-center gap-2 flex-wrap" data-testid={`tmpl-row-${it.id}`}>
                      {isEditing ? (
                        <>
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="flex-1 min-w-[220px] border border-[#d1cece] rounded-md px-2 py-1 text-sm bg-white"
                            data-testid="edit-title-input"
                            autoFocus
                          />
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={editRequired} onChange={(e) => setEditRequired(e.target.checked)} /> Required
                          </label>
                          <button onClick={() => saveEdit(it.id)} disabled={saving}
                            className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e] disabled:opacity-40"
                            data-testid="save-edit-btn">
                            <Save size={11} className="inline mr-1" /> Save
                          </button>
                          <button onClick={() => setEditing(null)}
                            className="text-xs text-[#050303]/50 hover:text-[#bf858e]"
                            data-testid="cancel-edit-btn">
                            <XIcon size={12} className="inline" /> Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 min-w-[220px] text-sm">
                            {it.title}
                            {it.required && <span className="text-[10px] text-red-600 ml-2">REQUIRED</span>}
                            {!it.is_default && <span className="text-[10px] text-[#bf858e] ml-2">CUSTOM</span>}
                          </div>
                          <button onClick={() => { setEditing(it.id); setEditTitle(it.title); setEditRequired(it.required); }}
                            className="text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                            data-testid={`edit-row-${it.id}`}>
                            <Pencil size={11} /> Rename
                          </button>
                          <button onClick={() => remove(it.id, it.title)}
                            className="text-xs text-[#050303]/40 hover:text-[#bf858e] flex items-center gap-1"
                            data-testid={`delete-row-${it.id}`}>
                            <Trash2 size={11} /> Remove
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
                {adding === stage && (
                  <div className="py-2 flex items-center gap-2 flex-wrap">
                    <input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="New document title (e.g. HOA Docs Received)"
                      className="flex-1 min-w-[220px] border border-[#d1cece] rounded-md px-2 py-1 text-sm bg-white"
                      data-testid="new-row-title"
                      autoFocus
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} /> Required
                    </label>
                    <button onClick={() => addRow(stage)} disabled={saving || !newTitle.trim()}
                      className="pill-btn text-xs bg-[#050303] text-white hover:bg-[#bf858e] disabled:opacity-40"
                      data-testid={`save-new-row-${stage.replace(/\s+/g, "-").toLowerCase()}`}>
                      <Save size={11} className="inline mr-1" /> Add
                    </button>
                    <button onClick={() => { setAdding(null); setNewTitle(""); }}
                      className="text-xs text-[#050303]/50 hover:text-[#bf858e]">
                      <XIcon size={12} className="inline" /> Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function NewTransactionDialog({ open, setOpen, onSelect }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [deals, setDeals] = useState([]);
  const [pickedDealId, setPickedDealId] = useState("");
  const [address, setAddress] = useState("");
  const [client, setClient] = useState("");
  const [price, setPrice] = useState("");
  const [stage, setStage] = useState("Listed");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset + fetch fresh deals every time it opens
    setMode("existing");
    setPickedDealId(""); setAddress(""); setClient(""); setPrice(""); setStage("Listed");
    api.get("/deals").then((r) => {
      // Prefer active deals (not yet Closed) at the top
      const sorted = [...r.data].sort((a, b) => {
        const rank = (d) => (d.stage === "Closed" ? 1 : 0);
        return rank(a) - rank(b) || (b.updated_at || "").localeCompare(a.updated_at || "");
      });
      setDeals(sorted);
    });
  }, [open]);

  const submitExisting = async () => {
    if (!pickedDealId) return;
    setBusy(true);
    try {
      // Priming the checklist by hitting the items endpoint auto-creates rows.
      await api.get(`/compliance/deals/${pickedDealId}/items`);
      onSelect(pickedDealId);
      setOpen(false);
      toast.success("Transaction opened");
    } catch { toast.error("Could not open transaction"); }
    finally { setBusy(false); }
  };

  const submitNew = async (e) => {
    e?.preventDefault?.();
    if (!address.trim() || !client.trim() || !price) {
      toast.error("Address, client, and price are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/deals", {
        title: address.trim().split(",")[0].slice(0, 60) || "New Listing",
        address: address.trim(),
        client: client.trim(),
        price: parseFloat(price),
        commission_pct: 3.0,
        stage,
        notes: "Created from Compliance Checklist",
        image_url: null,
      });
      // Prime the checklist for the fresh deal.
      await api.get(`/compliance/deals/${data.id}/items`);
      onSelect(data.id);
      setOpen(false);
      toast.success("Transaction created — folders ready");
    } catch {
      toast.error("Could not create transaction");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="new-txn-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">New Compliance Transaction</DialogTitle>
          <DialogDescription className="text-xs text-[#050303]/60">
            Pick an existing deal from your Pipeline or spin up a fresh property. Either path generates the three checklist folders.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b border-[#d1cece] mt-2">
          {[
            { k: "existing", label: "Existing Deal" },
            { k: "new", label: "New Property" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setMode(t.k)}
              className={`px-4 py-2 text-sm border-b-2 ${mode === t.k ? "border-[#050303] font-semibold" : "border-transparent text-[#050303]/50 hover:text-[#050303]"}`}
              data-testid={`txn-mode-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mode === "existing" ? (
          <div className="space-y-4 pt-4">
            <div>
              <Label>Pick a deal from your Pipeline</Label>
              <select
                value={pickedDealId}
                onChange={(e) => setPickedDealId(e.target.value)}
                className="w-full border border-[#d1cece] rounded-md px-3 py-2 text-sm bg-white mt-1"
                data-testid="pick-deal-select"
              >
                <option value="">— Select a deal —</option>
                {deals.length === 0 && <option disabled>No deals in your pipeline yet</option>}
                {deals.map((d) => (
                  <option key={d.id} value={d.id}>{`${d.title} · ${d.address} · ${d.stage}`}</option>
                ))}
              </select>
              <p className="text-xs text-[#050303]/50 mt-2">
                Opening a deal here generates its three checklist folders (Listing Documents, Under Contract, Closed) and any uploads you make will land in the broker&apos;s Pending Approvals queue.
              </p>
            </div>
            <button
              onClick={submitExisting}
              disabled={!pickedDealId || busy}
              className="w-full bg-[#050303] text-white pill-btn hover:bg-[#bf858e] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="open-existing-btn"
            >
              {busy ? "Opening…" : "Open Transaction"}
            </button>
          </div>
        ) : (
          <form onSubmit={submitNew} className="space-y-3 pt-4" data-testid="new-property-form">
            <div>
              <Label>Property Address</Label>
              <Input
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Peachtree St NE, Atlanta, GA 30303"
                data-testid="new-property-address"
              />
            </div>
            <div>
              <Label>Client Name</Label>
              <Input
                required
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Client full name"
                data-testid="new-property-client"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>List / Sale Price</Label>
                <Input
                  required
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="450000"
                  data-testid="new-property-price"
                />
              </div>
              <div>
                <Label>Starting Stage</Label>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full border border-[#d1cece] rounded-md px-3 py-2 text-sm bg-white mt-1"
                  data-testid="new-property-stage"
                >
                  {["Prospect", "CMA", "Listed", "Contract", "Closing"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-[#050303]/50">
              This creates a new deal in your Pipeline and immediately generates the three checklist folders (Listing Documents, Under Contract, Closed).
            </p>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#050303] text-white pill-btn hover:bg-[#bf858e] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="create-new-property-btn"
            >
              {busy ? "Creating…" : "Create Transaction"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
